import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { resolveGalleryDomain, type ResolvedGalleryDomain } from '../../services/galleryDomainService';
import { useStorageUrl } from '../../hooks/useStorageUrl';

export default function LoginPage() {
  const [accessCode, setAccessCode] = useState('');
  const [resolvedGallery, setResolvedGallery] = useState<ResolvedGalleryDomain | null>(null);
  const [isResolvingGallery, setIsResolvingGallery] = useState(true);
  const [galleryResolveError, setGalleryResolveError] = useState<string | null>(null);
  const [preferredViewMode, setPreferredViewMode] = useState<'2d' | '3d'>('3d');
  const { login, isLoading, error, clearError } = useAuthStore();
  const { url: logoUrl, loading: logoLoading } = useStorageUrl(resolvedGallery?.logoImagePath);

  useEffect(() => {
    let cancelled = false;

    async function loadGalleryDomain() {
      try {
        setIsResolvingGallery(true);
        setGalleryResolveError(null);
        const gallery = await resolveGalleryDomain();

        if (!cancelled) {
          setResolvedGallery(gallery);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to resolve gallery domain:', err);
          setResolvedGallery(null);
          setGalleryResolveError(err instanceof Error ? err.message : 'This gallery URL is not configured.');
        }
      } finally {
        if (!cancelled) {
          setIsResolvingGallery(false);
        }
      }
    }

    loadGalleryDomain();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!accessCode.trim() || !resolvedGallery) {
      return;
    }

    const success = await login(accessCode.trim(), resolvedGallery.galleryId, preferredViewMode);
    if (!success) {
      // Error is handled by the store
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setAccessCode(value);
    if (error) clearError();
  };

  const galleryName = isResolvingGallery
    ? 'Loading gallery'
    : resolvedGallery?.galleryName || 'Gallery unavailable';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          {resolvedGallery?.logoImagePath && (
            <div className="mx-auto mb-5 flex h-24 w-40 items-center justify-center">
              {logoLoading ? (
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              ) : logoUrl ? (
                <img
                  src={logoUrl}
                  alt={resolvedGallery.logoImageAlt || `${resolvedGallery.galleryName} logo`}
                  className="max-h-24 max-w-40 object-contain"
                />
              ) : null}
            </div>
          )}
          <h1 className="text-4xl font-bold tracking-normal text-gray-950">{galleryName}</h1>
          <a
            href="https://www.artvrllc.com"
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-block text-sm font-medium text-gray-500 hover:text-primary-700"
          >
            Powered by ArtVR
          </a>
          <p className="mt-5 text-gray-600">Enter your access code to begin your gallery experience.</p>
        </div>

        {/* Login Form */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="card p-6"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {galleryResolveError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              >
                <AlertCircle className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{galleryResolveError}</span>
              </motion.div>
            )}

            <div>
              <label htmlFor="accessCode" className="block text-sm font-medium text-gray-700 mb-1">
                Access Code
              </label>
              <input
                id="accessCode"
                type="text"
                value={accessCode}
                onChange={handleCodeChange}
                placeholder="Enter your access code"
                className={`input ${error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
                disabled={isLoading || isResolvingGallery || !!galleryResolveError}
                autoFocus
                autoComplete="off"
                spellCheck={false}
              />
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-2 flex items-center text-sm text-red-600"
                >
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {error}
                </motion.div>
              )}
            </div>

            <button
              type="submit"
              disabled={!accessCode.trim() || isLoading || isResolvingGallery || !resolvedGallery}
              className="w-full btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading || isResolvingGallery ? (
                <>
                  <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
                  <span>{isResolvingGallery ? 'Loading gallery...' : 'Validating...'}</span>
                </>
              ) : (
                'Enter Gallery'
              )}
            </button>

            <div className="text-center text-sm">
              {preferredViewMode === '3d' ? (
                <button
                  type="button"
                  onClick={() => setPreferredViewMode('2d')}
                  disabled={isLoading || isResolvingGallery || !!galleryResolveError}
                  className="font-medium text-gray-500 underline-offset-4 hover:text-primary-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Switch to 2D mode
                </button>
              ) : (
                <span className="text-gray-500">
                  2D mode selected.{' '}
                  <button
                    type="button"
                    onClick={() => setPreferredViewMode('3d')}
                    disabled={isLoading || isResolvingGallery || !!galleryResolveError}
                    className="font-medium text-gray-600 underline-offset-4 hover:text-primary-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Use 3D mode
                  </button>
                </span>
              )}
            </div>
          </form>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-center mt-6 text-sm text-gray-500"
        >
          Need an access code? Contact your gallery administrator.
        </motion.div>
      </motion.div>
    </div>
  );
}
